import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUser, useUploadSupportingDocument } from '../hooks';
import { Download, Upload, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/page-header';
import {
  InvoiceForm,
  InvoiceItemsTable,
  InvoicePreview,
  InvoiceItem,
  SupportingDocumentsPicker,
  SelectedSupportingDocument,
} from '../components/invoice-generator';
import { invoiceService } from '../services/invoiceService';
import jsPDF from 'jspdf';

interface InvoiceData {
  contractor: {
    name: string;
    email: string;
    address: string;
    taxId: string;
  };
  client: {
    name: string;
    address: string;
  };
  meta: {
    number: string;
    date: string;
  };
  items: InvoiceItem[];
  notes: string;
  invoiceMonth: string;
}

const InvoiceGenerator: React.FC = () => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: userProfile } = useUser(currentUser?.id || '');
  const previewRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [supportingDocuments, setSupportingDocuments] = useState<SelectedSupportingDocument[]>([]);
  // Set once the invoice exists on the server, so retrying failed document
  // uploads never creates a second invoice
  const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(null);
  const uploadSupportingDocument = useUploadSupportingDocument();


  const [invoice, setInvoice] = useState<InvoiceData>({
    contractor: {
      name: '',
      email: '',
      address: '',
      taxId: '',
    },
    client: {
      name: 'Prosigliere',
      address: '16517 Along Creek Cove\nAustin, TX 78717',
    },
    meta: {
      number: '',
      date: new Date().toISOString().split('T')[0],
    },
    items: [
      { id: 1, description: 'Professional Services', quantity: 1, rate: 0 },
    ],
    notes: 'Thank you!',
    invoiceMonth: new Date().toISOString().slice(0, 7),
  });

  useEffect(() => {
    if (userProfile) {
      setInvoice(prev => ({
        ...prev,
        contractor: {
          name: userProfile.name || '',
          email: userProfile.email || '',
          address: prev.contractor.address,
          taxId: prev.contractor.taxId,
        },
        items: prev.items.map(item => ({
          ...item,
          rate: userProfile.monthlyRate || 0,
        })),
      }));
      generateInvoiceNumber();
    }
  }, [userProfile]);

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const invoiceNumber = `INV-${year}${month}-${random}`;
    setInvoice(prev => ({ ...prev, meta: { ...prev.meta, number: invoiceNumber } }));
  };

  const handleFormChange = (field: string, value: string) => {
    const keys = field.split('.');
    if (keys.length === 2) {
      setInvoice(prev => ({
        ...prev,
        [keys[0]]: {
          ...(prev as any)[keys[0]],
          [keys[1]]: value,
        },
      }));
    } else {
      setInvoice(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleItemChange = (id: number, field: keyof InvoiceItem, value: string | number) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleAddItem = () => {
    const newId = Math.max(...invoice.items.map(i => i.id), 0) + 1;
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { id: newId, description: '', quantity: 1, rate: userProfile?.monthlyRate || 0 }],
    }));
  };

  const handleRemoveItem = (id: number) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id),
    }));
  };

  const handleAddSupportingDocuments = (files: File[]) => {
    setSupportingDocuments(prev => [...prev, ...files.map(file => ({ file }))]);
  };

  const handleRemoveSupportingDocument = (index: number) => {
    setSupportingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSupportingDocumentDescriptionChange = (index: number, description: string) => {
    setSupportingDocuments(prev =>
      prev.map((doc, i) => (i === index ? { ...doc, description } : doc))
    );
  };

  const generatePdfFromPreview = async (): Promise<jsPDF> => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });

    const element = previewRef.current;
    if (!element) {
      throw new Error('Preview element not found');
    }

    await pdf.html(element, {
      callback: () => {},
      x: 10,
      y: 10,
      width: 190,
      windowWidth: 800,
    });

    return pdf;
  };

  const handlePrint = async () => {
    try {
      const pdf = await generatePdfFromPreview();
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      // Revoke the blob URL after the new tab has had time to load it
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    }
  };

    // Validation logic
    const validateInvoice = (): string[] => {
      const errors: string[] = [];

      // Contractor name is required
      if (!invoice.contractor.name.trim()) {
        errors.push('Contractor name is required');
      }

      // Invoice month is required
      if (!invoice.invoiceMonth) {
        errors.push('Invoice month is required');
      }

      // All line items must have a description
      const itemsWithoutDescription = invoice.items.filter(item => !item.description.trim());
      if (itemsWithoutDescription.length > 0) {
        errors.push('All line items must have a description');
      }

      // Quantity must be greater than 0
      const itemsWithZeroQuantity = invoice.items.filter(item => item.quantity <= 0);
      if (itemsWithZeroQuantity.length > 0) {
        errors.push('Quantity must be greater than 0 for all line items');
      }

      // Rate must be non-negative
      const itemsWithNegativeRate = invoice.items.filter(item => item.rate < 0);
      if (itemsWithNegativeRate.length > 0) {
        errors.push('Rate cannot be negative');
      }

      return errors;
    };

    // Check if form is valid for submission
    const isFormValid = useMemo(() => {
      // Contractor name is required
      if (!invoice.contractor.name.trim()) return false;

      // Invoice month is required
      if (!invoice.invoiceMonth) return false;

      // All line items must have a description
      if (invoice.items.some(item => !item.description.trim())) return false;

      // Quantity must be greater than 0
      if (invoice.items.some(item => item.quantity <= 0)) return false;

      // Rate must be non-negative
      if (invoice.items.some(item => item.rate < 0)) return false;

      return true;
    }, [invoice]);

    const handleSubmitForReview = async () => {
      if (loading) return; // Prevent double submission

      // Validate before submission
      const errors = validateInvoice();
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors([]);
    
      try {
        setLoading(true);

      let invoiceId = createdInvoiceId;

      if (!invoiceId) {
        // Create PDF using shared helper
        const pdf = await generatePdfFromPreview();
        const pdfBlob = pdf.output('blob');
        const file = new File([pdfBlob], `invoice-${invoice.meta.number}.pdf`, {
          type: 'application/pdf',
        });

        // Upload the invoice via the service layer
        const createdInvoice = await invoiceService.create(
          {
            invoiceMonth: invoice.invoiceMonth,
            notes: `Generated invoice - ${invoice.meta.number}`,
            vendorName: invoice.contractor.name,
          },
          file
        );
        invoiceId = createdInvoice.id;
        setCreatedInvoiceId(invoiceId);
      }

      // Attach the supporting documents to the created invoice
      const failedDocuments: SelectedSupportingDocument[] = [];
      const uploadErrors: string[] = [];
      for (const doc of supportingDocuments) {
        try {
          await uploadSupportingDocument.mutateAsync({
            invoiceId,
            file: doc.file,
            description: doc.description?.trim() || undefined,
          });
        } catch (uploadError: any) {
          console.error('Failed to upload supporting document:', doc.file.name, uploadError);
          failedDocuments.push(doc);
          const reason =
            uploadError?.response?.data?.message || uploadError?.message || 'Unknown error';
          uploadErrors.push(`${doc.file.name}: ${reason}`);
        }
      }

      if (failedDocuments.length > 0) {
        // Keep only the documents that still need to be uploaded
        setSupportingDocuments(failedDocuments);
        setValidationErrors([t('generator.error.documentUploadFailed'), ...uploadErrors]);
      }

      // Invalidate and refetch invoice queries to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['invoice-statistics'] }),
      ]);

      // Refetch to ensure data is loaded before navigation
      await queryClient.refetchQueries({ queryKey: ['invoices'] });

      // Small delay to show success state before redirect
      await new Promise(resolve => setTimeout(resolve, 300));

      if (failedDocuments.length > 0) {
        // Keep the user on the screen so the upload failure stays visible
        setLoading(false);
        return;
      }

      // Redirect to invoices page
      navigate('/invoices');
    } catch (err: any) {
      console.error('Failed to submit invoice:', err);
      alert(t('generator.error.submitFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t('generator.title')}
        description={t('generator.description')}
      >
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} disabled={loading}>
            <Download className="h-4 w-4 mr-2" />
            {t('generator.printSavePdf')}
          </Button>
          <Button onClick={handleSubmitForReview} disabled={loading || !isFormValid}>
            {loading ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {tCommon('processing')}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {createdInvoiceId
                  ? t('generator.retryDocumentUploads')
                  : t('generator.submitForReview')}
              </>
            )}
          </Button>
        </div>
            </PageHeader>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <h4 className="font-medium text-destructive">Please fix the following errors:</h4>
                    <ul className="mt-2 list-disc list-inside text-sm text-destructive/90">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <div className="space-y-6">
          <InvoiceForm
            data={{
              contractor: invoice.contractor,
              client: invoice.client,
              meta: invoice.meta,
              notes: invoice.notes,
              invoiceMonth: invoice.invoiceMonth,
            }}
            onChange={handleFormChange}
          />

          <InvoiceItemsTable
            items={invoice.items}
            onItemChange={handleItemChange}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
          />

          <SupportingDocumentsPicker
            documents={supportingDocuments}
            onAddFiles={handleAddSupportingDocuments}
            onRemoveDocument={handleRemoveSupportingDocument}
            onDescriptionChange={handleSupportingDocumentDescriptionChange}
            disabled={loading}
          />
        </div>

        {/* Right Column - Preview */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <h2 className="text-lg font-semibold mb-4">{t('generator.preview.title')}</h2>
          <div 
            className="overflow-auto border rounded-lg bg-gray-100 p-4" 
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            <div className="origin-top-left" style={{ transform: 'scale(0.7)', transformOrigin: 'top left' }}>
              <InvoicePreview
                ref={previewRef}
                data={{
                  contractor: invoice.contractor,
                  client: invoice.client,
                  meta: invoice.meta,
                  notes: invoice.notes,
                  invoiceMonth: invoice.invoiceMonth,
                }}
                items={invoice.items}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default InvoiceGenerator;
